import React, { useState, useEffect, useContext } from 'react';
import { API, AuthContext } from '../App';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { ClipboardCheck, AlertCircle, CheckCircle, Wrench, Plus, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-gray-500">Loading dashboard...</div>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Total Inspections',
      value: stats?.total_inspections || 0,
      icon: ClipboardCheck,
      color: 'bg-blue-500',
      textColor: 'text-blue-600',
      bgColor: 'bg-blue-50',
      testId: 'total-inspections-stat'
    },
    {
      title: 'Passed',
      value: stats?.passed_inspections || 0,
      icon: CheckCircle,
      color: 'bg-green-500',
      textColor: 'text-green-600',
      bgColor: 'bg-green-50',
      testId: 'passed-inspections-stat'
    },
    {
      title: 'Service Required',
      value: stats?.service_required || 0,
      icon: Wrench,
      color: 'bg-yellow-500',
      textColor: 'text-yellow-600',
      bgColor: 'bg-yellow-50',
      testId: 'service-required-stat'
    },
    {
      title: 'Failed',
      value: stats?.failed_inspections || 0,
      icon: AlertCircle,
      color: 'bg-red-500',
      textColor: 'text-red-600',
      bgColor: 'bg-red-50',
      testId: 'failed-inspections-stat'
    },
  ];

  return (
    <div className="space-y-6 fade-in" data-testid="dashboard-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600 mt-1">Welcome back, {user?.name}</p>
        </div>
        <Button
          onClick={() => navigate('/new-inspection')}
          data-testid="new-inspection-button"
          className="bg-blue-600 hover:bg-blue-700 text-white h-11 px-6 font-semibold shadow-lg hover:shadow-xl transition-all"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Inspection
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((card, index) => (
          <Card
            key={index}
            data-testid={card.testId}
            className="border-0 shadow-lg hover:shadow-xl transition-shadow cursor-pointer"
            style={{ animationDelay: `${index * 0.1}s` }}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">{card.title}</p>
                  <p className={`text-3xl font-bold ${card.textColor}`}>{card.value}</p>
                </div>
                <div className={`${card.bgColor} p-3 rounded-xl`}>
                  <card.icon className={`w-7 h-7 ${card.textColor}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="border-0 shadow-lg">
        <CardHeader className="border-b bg-gradient-to-r from-blue-50 to-orange-50">
          <CardTitle className="flex items-center">
            <TrendingUp className="w-5 h-5 mr-2 text-blue-600" />
            Recent Inspections
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="recent-inspections-table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Plate Number</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Driver</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Inspector</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {stats?.recent_inspections && stats.recent_inspections.length > 0 ? (
                  stats.recent_inspections.map((inspection, index) => (
                    <tr
                      key={index}
                      onClick={() => navigate(`/inspection/${inspection.id}`)}
                      data-testid={`inspection-row-${index}`}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{inspection.plate_number}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{inspection.driver_name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{inspection.inspector_name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{inspection.inspection_date}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`status-badge ${
                          inspection.overall_status === 'Pass' ? 'status-pass' :
                          inspection.overall_status === 'Service Required' ? 'status-service' :
                          'status-failed'
                        }`}>
                          {inspection.overall_status}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                      No inspections yet. Create your first inspection to get started.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default Dashboard;